import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ButtonComponent,
  ConfirmDialogService,
  ToastService,
} from '@dental-ui/ui';
import {
  LucideChevronLeft,
  LucideChevronRight,
  LucideList,
  LucidePencil,
  LucidePlus,
  LucideRefreshCw,
  LucideSearch,
  LucideTrash2,
  LucideX,
} from '@lucide/angular';
import {
  PortalPackagesApiService,
  PackageDto,
  PackageFeatureDto,
  FeatureItem,
} from '../data-access/portal-packages-api.service';

const PAGE_SIZE = 10;

@Component({
  selector: 'lib-portal-packages',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    LucideChevronLeft,
    LucideChevronRight,
    LucideList,
    LucidePencil,
    LucidePlus,
    LucideRefreshCw,
    LucideSearch,
    LucideTrash2,
    LucideX,
  ],
  templateUrl: './portal-packages.component.html',
  styleUrl: './portal-packages.component.scss',
})
export class PortalPackagesComponent implements OnInit {
  private readonly api = inject(PortalPackagesApiService);
  private readonly toast = inject(ToastService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly items = signal<PackageDto[]>([]);
  readonly loading = signal(false);
  readonly totalPages = signal(0);
  readonly page = signal(0);
  searchTerm = '';

  readonly showFormDialog = signal(false);
  readonly formSaving = signal(false);
  readonly isEdit = signal(false);
  readonly formModel = signal<PackageDto>({
    id: '',
    name: '',
    isActive: true,
    isLab: false,
    isClinic: false,
    isCad: false,
    description: null,
    currencyType: 'USD',
    monthlyPrice: 0,
    yearlyPrice: 0,
    monthlyDiscount: 0,
    yearlyDiscount: 0,
    totalMonthlyPrice: 0,
    totalYearlyPrice: 0,
    ModifiedBy: 0,
    IsDeleted: false,
  });

  readonly showFeaturesDialog = signal(false);
  readonly selectedPackage = signal<PackageDto | null>(null);
  readonly packageFeatures = signal<PackageFeatureDto[]>([]);
  readonly featuresLoading = signal(false);
  readonly featuresPage = signal(0);
  readonly featuresTotalPages = signal(0);

  readonly showAddFeatureDialog = signal(false);
  readonly allFeatures = signal<FeatureItem[]>([]);
  readonly allFeaturesLoading = signal(false);
  readonly selectedFeatureId = signal('');

  readonly availableFeatures = computed(() => {
    const existing = new Set(this.packageFeatures().map((pf) => pf.featureId));
    return this.allFeatures().filter((f) => !existing.has(f.id));
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.getPackages(this.page(), PAGE_SIZE, this.searchTerm).subscribe({
      next: (res) => {
        this.items.set(res.data);
        this.totalPages.set(res.totalPage);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.toast.error('Paketler yüklenirken hata oluştu');
      },
    });
  }

  onSearch(): void {
    this.page.set(0);
    this.load();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.page.set(0);
    this.load();
  }

  prevPage(): void {
    if (this.page() > 0) {
      this.page.update((p) => p - 1);
      this.load();
    }
  }

  nextPage(): void {
    if (this.page() < this.totalPages() - 1) {
      this.page.update((p) => p + 1);
      this.load();
    }
  }

  openAdd(): void {
    this.isEdit.set(false);
    this.formModel.set({
      id: '',
      name: '',
      isActive: true,
      isLab: false,
      isClinic: false,
      isCad: false,
      description: null,
      currencyType: 'USD',
      monthlyPrice: 0,
      yearlyPrice: 0,
      monthlyDiscount: 0,
      yearlyDiscount: 0,
      totalMonthlyPrice: 0,
      totalYearlyPrice: 0,
      ModifiedBy: 0,
      IsDeleted: false,
    });
    this.showFormDialog.set(true);
  }

  openEdit(item: PackageDto): void {
    this.isEdit.set(true);
    this.formModel.set({ ...item });
    this.showFormDialog.set(true);
  }

  closeFormDialog(): void {
    this.showFormDialog.set(false);
  }

  calcTotals(): void {
    const m = this.formModel();
    const monthlyDiscount = m.monthlyDiscount ?? 0;
    const yearlyDiscount = m.yearlyDiscount ?? 0;
    const monthly = m.monthlyPrice - (m.monthlyPrice * Math.min(Math.max(monthlyDiscount, 0), 100)) / 100;
    const yearly = m.yearlyPrice - (m.yearlyPrice * Math.min(Math.max(yearlyDiscount, 0), 100)) / 100;
    this.formModel.update((prev) => ({
      ...prev,
      totalMonthlyPrice: Math.round(monthly * 100) / 100,
      totalYearlyPrice: Math.round(yearly * 100) / 100,
    }));
  }

  updateField<K extends keyof PackageDto>(key: K, value: PackageDto[K]): void {
    this.formModel.update((prev) => ({ ...prev, [key]: value }));
    this.calcTotals();
  }

  save(): void {
    const model = this.formModel();
    this.formSaving.set(true);
    const call = this.isEdit()
      ? this.api.updatePackage(model)
      : this.api.createPackage(model);

    call.subscribe({
      next: () => {
        this.formSaving.set(false);
        this.toast.success(this.isEdit() ? 'Paket güncellendi' : 'Paket eklendi');
        this.showFormDialog.set(false);
        this.load();
      },
      error: () => {
        this.formSaving.set(false);
        this.toast.error('İşlem sırasında hata oluştu');
      },
    });
  }

  delete(item: PackageDto): void {
    this.confirmDialog
      .confirm({
        title: 'Paketi Sil',
        message: `"${item.name}" paketini silmek istediğinizden emin misiniz?`,
        confirmText: 'Sil',
        cancelText: 'İptal',
        confirmButtonType: 'danger',
      })
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.api.deletePackage(item.id).subscribe({
          next: () => {
            this.toast.success('Paket silindi');
            this.load();
          },
          error: () => this.toast.error('Silme işlemi başarısız'),
        });
      });
  }

  openFeaturesDialog(item: PackageDto): void {
    this.selectedPackage.set(item);
    this.featuresPage.set(0);
    this.packageFeatures.set([]);
    this.showFeaturesDialog.set(true);
    this.loadPackageFeatures();
  }

  closeFeaturesDialog(): void {
    this.showFeaturesDialog.set(false);
  }

  loadPackageFeatures(): void {
    const pkg = this.selectedPackage();
    if (!pkg) return;
    this.featuresLoading.set(true);
    this.api.getPackageFeatures(pkg.name, this.featuresPage(), PAGE_SIZE).subscribe({
      next: (res) => {
        this.packageFeatures.set(res.data);
        this.featuresTotalPages.set(res.totalPage);
        this.featuresLoading.set(false);
      },
      error: () => {
        this.featuresLoading.set(false);
        this.toast.error('Paket özellikleri yüklenirken hata oluştu');
      },
    });
  }

  prevFeaturesPage(): void {
    if (this.featuresPage() > 0) {
      this.featuresPage.update((p) => p - 1);
      this.loadPackageFeatures();
    }
  }

  nextFeaturesPage(): void {
    if (this.featuresPage() < this.featuresTotalPages() - 1) {
      this.featuresPage.update((p) => p + 1);
      this.loadPackageFeatures();
    }
  }

  deletePackageFeature(pf: PackageFeatureDto): void {
    this.confirmDialog
      .confirm({
        title: 'Özelliği Kaldır',
        message: `"${pf.featureName}" özelliğini paketten kaldırmak istediğinizden emin misiniz?`,
        confirmText: 'Kaldır',
        cancelText: 'İptal',
        confirmButtonType: 'danger',
      })
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.api.deletePackageFeature(pf.id).subscribe({
          next: () => {
            this.toast.success('Özellik paketten kaldırıldı');
            this.loadPackageFeatures();
          },
          error: () => this.toast.error('Kaldırma işlemi başarısız'),
        });
      });
  }

  openAddFeatureDialog(): void {
    this.selectedFeatureId.set('');
    this.showAddFeatureDialog.set(true);
    this.allFeaturesLoading.set(true);
    this.api.getAllFeatures().subscribe({
      next: (features) => {
        this.allFeatures.set(features);
        this.allFeaturesLoading.set(false);
      },
      error: () => {
        this.allFeaturesLoading.set(false);
        this.toast.error('Özellikler yüklenirken hata oluştu');
      },
    });
  }

  closeAddFeatureDialog(): void {
    this.showAddFeatureDialog.set(false);
  }

  savePackageFeature(): void {
    const pkg = this.selectedPackage();
    const featureId = this.selectedFeatureId();
    if (!pkg || !featureId) {
      this.toast.error('Lütfen bir özellik seçiniz');
      return;
    }
    this.api.createPackageFeature(pkg.id, featureId).subscribe({
      next: () => {
        this.toast.success('Özellik pakete eklendi');
        this.showAddFeatureDialog.set(false);
        this.loadPackageFeatures();
      },
      error: () => this.toast.error('Ekleme işlemi başarısız'),
    });
  }

  formatPrice(value: number, currency: string): string {
    return `${value.toFixed(2)} ${currency}`;
  }
}
